import { useObservableState } from 'observable-hooks';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import useDefaultTableTopbar from '../../../components/common/table/default-topbar';
import Table, { TableColumn } from '../../../components/common/table/table';
import { ActiveFilter } from '../../../components/common/table/active-filter';
import { DefaultFilter } from '../../../components/common/table/default-filter';
import { Option } from '../../../components/form/elements/select';
import { Product } from '../../../interfaces/product';
import {
  WAREHOUSE_PRODUCTS_CREATE,
  WAREHOUSE_PRODUCTS_VIEW,
} from '../../../router/routes';
import { useService } from '../../../services/services';
import { route } from '../../../services/utils';
import css from './index.module.css';
import { ProductObtainType } from '../../../enums/product-obtain-type';
import Classifier from '../../../enums/classifier';
import ShowIfAllowed from '../../../components/common/show-if-allowed';

export default function ProductsList() {
  const { t } = useTranslation();
  const { farmId, obtainTypeId } = useParams<{
    farmId: string;
    obtainTypeId: ProductObtainType;
  }>();
  const { productService } = useService();

  const [TopbarComponent] = useDefaultTableTopbar(
    route(WAREHOUSE_PRODUCTS_CREATE, { farmId, obtainTypeId })
  );

  const [productCategories] = useObservableState<Option[]>(
    () =>
      productService
        .usedProductCategories(farmId, {
          product_obtain_type_id: obtainTypeId,
          classifier_key:
            obtainTypeId === ProductObtainType.RECYCLE_GAINED
              ? Classifier.OBTAINED_PRODUCT_CATEGORIES_AND_TYPES
              : Classifier.PRODUCT_CATEGORIES_AND_TYPES,
        })
        .pipe(
          map((values) =>
            values.map((category) => {
              return { value: category.id, label: category.value };
            })
          )
        ),
    null
  );

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) => {
      return productService.index(farmId, {
        obtain_type_id: obtainTypeId,
        page,
        sort,
        desc,
        ...filter,
      });
    },
    [productService, farmId, obtainTypeId]
  );

  const tableColumns = (): TableColumn<Product>[] => {
    const columns: TableColumn<Product>[] = [];

    columns.push(
      {
        accessor: 'category.value',
        header: t('pages.warehouse.products.product_category'),
        sortable: true,
        filter: (
          <DefaultFilter
            options={productCategories}
            name="product_categories"
          />
        ),
        className: css['strong'],
      },
      {
        accessor: 'type.value',
        header: t('pages.warehouse.products.product_type'),
        sortable: true,
        className: css['strong'],
      },
      {
        accessor: 'name',
        header: t('pages.warehouse.products.name'),
        sortable: true,
        className: css['strong'],
      },
      {
        accessor: 'unit',
        header: t('pages.warehouse.products.unit'),
      }
    );

    if (
      obtainTypeId === ProductObtainType.PRODUCED ||
      obtainTypeId === ProductObtainType.RECYCLE_GAINED
    ) {
      columns.push({
        accessor: 'planned_price',
        header: t('pages.warehouse.products.price'),
      });
    }

    columns.push({
      accessor: 'active',
      header: t('pages.warehouse.products.isActual'),
      filter: <ActiveFilter />,
    });

    return columns;
  };

  return (
    <div className={css['body']}>
      <ShowIfAllowed ability={{ title: 'create product' }}>
        {TopbarComponent}
      </ShowIfAllowed>
      <Table<Product>
        request={request}
        keyAccessor="id"
        columns={tableColumns()}
        viewRoute={route(WAREHOUSE_PRODUCTS_VIEW, {
          farmId,
          obtainTypeId,
        })}
        viewRecordAbility={{ title: 'view product' }}
      />
    </div>
  );
}
